import { css, html, LitElement, nothing } from 'lit'
import { property, state } from 'lit/decorators.js'
import { safeCustomElement } from '@/utilities/custom-element'
import '@/components/Avatar'
import '@/components/AvatarGroup'
import '@/components/Icon'
import '@/components/Modal'
import '@/components/Modal/ModalHeader'
import '@/components/ScrollContainer'

export interface SocialPersonRef {
  firstname: string
  lastname: string
  listingtype: string
  postSlug: string
  avatarSrc?: string
  avatarText?: string
  href?: string
}

export interface SocialPostItem {
  postId: string
  url: string
  thumbnail: string
  engagementRate: string
  likes: number
  comments: number
  postedBy: SocialPersonRef[]
  mentions: SocialPersonRef[]
}

@safeCustomElement('mdc-social-card')
export class MdcSocialCard extends LitElement {
  @property({ type: String, attribute: 'post-id' }) postId = ''
  @property({ type: String }) url = ''
  @property({ type: String }) thumbnail = ''
  @property({ type: String, attribute: 'engagement-rate' }) engagementRate = ''
  @property({ type: Number }) likes = 0
  @property({ type: Number }) comments = 0
  @property({ type: String }) caption = ''
  @property({ type: Array, attribute: false }) postedBy: SocialPersonRef[] = []
  @property({ type: Array, attribute: false }) mentions: SocialPersonRef[] = []

  @state() private _modalOpenFor: string | null = null
  @state() private _thumbnailError = false

  firstUpdated() {
    const img = this.shadowRoot?.querySelector(
      '.mdc-social-card__embed img'
    ) as HTMLImageElement | null
    if (img?.complete && img.naturalWidth === 0) {
      this._thumbnailError = true
    }
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('thumbnail')) {
      const oldThumbnail = changed.get('thumbnail') as string
      if (oldThumbnail && oldThumbnail !== this.thumbnail) {
        this._thumbnailError = false
      }
    }
  }

  static styles = css`
    :host {
      display: block;
      max-width: 320px;
      height: 100%;
    }

    .mdc-social-card {
      box-sizing: border-box;
      border: 1px solid var(--color-functional-gray-200);
      border-radius: var(--radius-md, 0.5rem);
      background-color: var(--color-functional-gray-white);
      overflow: hidden;
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    .mdc-social-card__embed {
      position: relative;
      width: 100%;
    }

    .mdc-social-card__embed img {
      width: 100%;
      display: block;
    }

    .mdc-social-card__embed--placeholder {
      display: flex;
      align-items: center;
      justify-content: center;
      aspect-ratio: 1 / 1;
      background-color: var(--color-functional-gray-100);
      border-bottom: 1px solid var(--color-functional-gray-200);
    }

    .mdc-social-card__embed-fallback {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--spacing-050, 0.5rem);
      color: var(--color-functional-gray-600);
      font-family: var(--typography-font-family-sans);
      font-size: var(--typography-font-size-075);
      font-weight: 450;
    }

    .mdc-social-card__body {
      padding: var(--spacing-100, 1rem);
      display: flex;
      flex-direction: column;
      gap: var(--spacing-125, 1.25rem);
      flex: 1;
    }

    .mdc-social-card__metrics {
      display: flex;
      gap: var(--spacing-100, 1rem);
      flex-wrap: wrap;
      margin-top: auto;
    }

    .mdc-social-card__metric {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-012-and-half, 0.125rem);
    }

    .mdc-social-card__metric-value {
      font-family: var(--typography-font-family-sans);
      font-size: var(--typography-font-size-087-and-half);
      font-weight: 600;
      line-height: 1.3;
      color: var(--color-functional-gray-black);
    }

    .mdc-social-card__metric-label {
      font-family: var(--typography-font-family-sans);
      font-size: var(--typography-font-size-062-and-half);
      font-weight: 450;
      line-height: 1.3;
      color: var(--color-functional-gray-600);
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    .mdc-social-card__section-label {
      font-family: var(--typography-font-family-sans);
      font-size: var(--typography-font-size-075);
      font-weight: 400;
      line-height: 1.3;
      color: var(--color-functional-gray-600);
      margin: 0;
    }

    /* "Posted by" is the primary author credit — bold + darker so it reads above the lighter
       "Mentions / Tags" label below it. */
    .mdc-social-card__section-label--primary {
      font-weight: 700;
      color: var(--color-functional-gray-black, #111);
    }

    .mdc-social-card__people {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-025, 0.25rem);
    }

    /* Post caption — a short (2-line) teaser of the account's own text, between the
       "Posted by" credit and the metrics row. The slot is ALWAYS rendered at a FIXED
       2-line height, so a post with no caption or a single-line caption reserves the
       exact same space and the card height never changes with caption length. The
       full post is one click away via the card image (which links to Instagram). */
    .mdc-social-card__caption {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      height: 2.8em;
      margin: 0;
      font-family: var(--typography-font-family-sans);
      font-size: var(--typography-font-size-075);
      line-height: 1.4;
      color: var(--color-functional-gray-600);
      overflow-wrap: anywhere;
    }


    .mdc-social-card__section-label-link {
      font: inherit;
      /* Matches the bold "Posted by" label it sits inside (e.g. "Posted by 2 people"). */
      font-weight: 700;
      color: inherit;
      text-decoration: underline;
      text-underline-position: under;
      cursor: pointer;
      background: none;
      border: none;
      padding: 0;
    }

    .mdc-social-card__mentions-trigger {
      cursor: pointer;
      background: none;
      border: none;
      padding: 0;
    }

    a {
      color: inherit;
      text-decoration: none;
    }

    a:hover {
      text-decoration: underline;
    }

    .mdc-social-card__modal-list {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-050, 0.5rem);
    }

    .mdc-social-card__modal-list a {
      display: block;
    }

  `

  private _formatNumber(n: number): string {
    return n.toLocaleString()
  }

  // wrap=true lets a long name break to multiple lines instead of truncating — used for the single
  // (non-scrollable) "Posted by" / "Mentions" person, where a long name would otherwise be cut off.
  private _renderPerson(person: SocialPersonRef, wrap = false) {
    const fullName = `${person.firstname} ${person.lastname}`.trim()
    const avatar = html`
      <mdc-avatar
        src=${person.avatarSrc ?? ''}
        text=${person.avatarText ?? ''}
        alt=${fullName}
        title=${fullName}
        subtitle=${person.listingtype}
        ?wrap-name=${wrap}
      ></mdc-avatar>
    `
    return person.href ? html`<a href=${person.href}>${avatar}</a>` : avatar
  }

  private _renderPostedBy() {
    if (!this.postedBy.length) return nothing

    if (this.postedBy.length === 1) {
      return this._renderPerson(this.postedBy[0], true)
    }

    return html`
      <mdc-scroll-container>
        ${this.postedBy.map((p) => this._renderPerson(p))}
      </mdc-scroll-container>
    `
  }

  private _renderMentions() {
    if (!this.mentions.length) return nothing

    // Any number of mentions (including a single one) renders as the thumbnail cluster that opens the
    // Mentions & Tags modal on click — keeping mentions visually subordinate to the primary "Posted by"
    // author, and consistent whether there's one tag or many.
    return html`
      <button
        class="mdc-social-card__mentions-trigger"
        @click=${() => {
          this._modalOpenFor = `${this.postId}-mentions`
        }}
      >
        <mdc-avatar-group>
          ${this.mentions.map(
            (m) => html`
            <mdc-avatar
              src=${m.avatarSrc ?? ''}
              text=${m.avatarText ?? ''}
              alt=${`${m.firstname} ${m.lastname}`.trim()}
            ></mdc-avatar>
          `
          )}
        </mdc-avatar-group>
      </button>
    `
  }

  private _renderPostedByModal() {
    if (this.postedBy.length <= 1) return nothing

    const isOpen = this._modalOpenFor === `${this.postId}-postedby`

    return html`
      <mdc-modal
        ?open=${isOpen}
        @close=${() => {
          this._modalOpenFor = null
        }}
      >
        <mdc-modal-header slot="header" size="md">Posted by ${this.postedBy.length} people</mdc-modal-header>
        <div class="mdc-social-card__modal-list">
          ${this.postedBy.map((p) => this._renderPerson(p))}
        </div>
      </mdc-modal>
    `
  }

  private _renderMentionsModal() {
    // Render for any mention count (including one) — the single-mention thumbnail now opens this modal too.
    if (!this.mentions.length) return nothing

    const isOpen = this._modalOpenFor === `${this.postId}-mentions`

    return html`
      <mdc-modal
        ?open=${isOpen}
        @close=${() => {
          this._modalOpenFor = null
        }}
      >
        <mdc-modal-header slot="header" size="md">Mentions & Tags</mdc-modal-header>
        <div class="mdc-social-card__modal-list">
          ${this.mentions.map((m) => {
            const fullName = `${m.firstname} ${m.lastname}`.trim()
            const avatar = html`
              <mdc-avatar
                src=${m.avatarSrc ?? ''}
                text=${m.avatarText ?? ''}
                alt=${fullName}
                title=${fullName}
                subtitle=${m.listingtype}
              ></mdc-avatar>
            `
            return m.href ? html`<a href=${m.href}>${avatar}</a>` : avatar
          })}
        </div>
      </mdc-modal>
    `
  }

  render() {
    const hasThumbnail = this.thumbnail && !this._thumbnailError
    return html`
      <div class="mdc-social-card">
        ${
          this.url
            ? html`
          <a
            class="mdc-social-card__embed${hasThumbnail ? '' : ' mdc-social-card__embed--placeholder'}"
            href=${this.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            ${
              hasThumbnail
                ? html`<img src=${this.thumbnail} alt="Instagram post" loading="lazy" @error=${() => {
                    this._thumbnailError = true
                  }} />`
                : html`
                  <div class="mdc-social-card__embed-fallback">
                    <mdc-icon name="instagram" size="lg"></mdc-icon>
                    <span>View on Instagram</span>
                  </div>
                `
            }
          </a>
        `
            : nothing
        }

        <div class="mdc-social-card__body">
          ${
            this.postedBy.length
              ? html`
              <div class="mdc-social-card__people">
                <p class="mdc-social-card__section-label mdc-social-card__section-label--primary">Posted by${
                  this.postedBy.length > 1
                    ? html` <button class="mdc-social-card__section-label-link" @click=${() => {
                        this._modalOpenFor = `${this.postId}-postedby`
                      }}>${this.postedBy.length} people</button>`
                    : nothing
                }</p>
                ${this._renderPostedBy()}
              </div>
            `
              : nothing
          }

          ${
            this.mentions.length
              ? html`
              <div class="mdc-social-card__people">
                <p class="mdc-social-card__section-label">Mentions / Tags</p>
                ${this._renderMentions()}
              </div>
            `
              : nothing
          }

          <p class="mdc-social-card__caption">${this.caption}</p>

          <div class="mdc-social-card__metrics">
            <div class="mdc-social-card__metric">
              <span class="mdc-social-card__metric-value">${this.engagementRate}%</span>
              <span class="mdc-social-card__metric-label">Engagement</span>
            </div>
            <div class="mdc-social-card__metric">
              <span class="mdc-social-card__metric-value">${this._formatNumber(this.likes)}</span>
              <span class="mdc-social-card__metric-label">Likes</span>
            </div>
            <div class="mdc-social-card__metric">
              <span class="mdc-social-card__metric-value">${this._formatNumber(this.comments)}</span>
              <span class="mdc-social-card__metric-label">Comments</span>
            </div>
          </div>
        </div>

        ${this._renderPostedByModal()}
        ${this._renderMentionsModal()}
      </div>
    `
  }
}
